import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cn, timeAgo, formatDate, formatDuration, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import type { Device } from '@/types';
import {
  Terminal as TerminalIcon,
  Play,
  RotateCw,
  Copy,
  Download,
  Trash2,
  Search,
  Plus,
  X,
  Shield,
  Clock,
  HardDrive,
  Wifi,
  CornerDownLeft,
  ChevronRight,
  History,
  HelpCircle,
  Check,
  AlertCircle,
  ArrowLeft,
  Monitor,
  Activity,
  Maximize2,
  Minimize2,
} from 'lucide-react';

interface CommandOutput {
  id: string;
  timestamp: string;
  type: 'prompt' | 'output' | 'error' | 'system';
  content: string;
  promptText?: string;
}

interface TerminalTab {
  id: string;
  title: string;
  type: 'powershell' | 'cmd';
  workingDir: string;
  outputs: CommandOutput[];
  commandHistory: string[];
  historyIndex: number;
  connectionStatus: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  exitCode: number | null;
  isExecuting: boolean;
}

interface TerminalProps {
  type?: string;
}

export function Terminal({ type: propType }: TerminalProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [device, setDevice] = useState<Device | null>(null);
  const [loadingDevice, setLoadingDevice] = useState(true);

  // Tab management
  const initialType = (propType === 'cmd' || propType === 'powershell') ? propType : 'powershell';
  const [tabs, setTabs] = useState<TerminalTab[]>([
    {
      id: 'tab-1',
      title: initialType === 'powershell' ? 'PowerShell 1' : 'CMD 1',
      type: initialType,
      workingDir: 'C:\\Users\\admin',
      outputs: [],
      commandHistory: [],
      historyIndex: -1,
      connectionStatus: 'connecting',
      exitCode: 0,
      isExecuting: false,
    },
  ]);
  const [activeTabId, setActiveTabId] = useState<string>('tab-1');

  // Active input and controls
  const [inputVal, setInputVal] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Session timer (counts up) & timeout timer (counts down from 30 min)
  const [sessionSeconds, setSessionSeconds] = useState(0);

  // Modals
  const [showNewTabModal, setShowNewTabModal] = useState(false);
  const [newTabType, setNewTabType] = useState<'powershell' | 'cmd'>('powershell');

  const outputEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  // Fetch device details
  useEffect(() => {
    let isMounted = true;
    async function loadDevice() {
      setLoadingDevice(true);
      try {
        if (id) {
          const d = await mockApi.getDevice(id);
          if (isMounted) {
            setDevice(d);
            setLoadingDevice(false);
          }
        } else {
          // Fallback: fetch devices and pick first
          const res = await mockApi.getDevices({ pageSize: 1 });
          if (isMounted) {
            if (res.data.length > 0) setDevice(res.data[0]);
            setLoadingDevice(false);
          }
        }
      } catch (error) {
        console.error('Error loading device data:', error);
        if (isMounted) {
          setLoadingDevice(false);
        }
      }
    }
    loadDevice();
    return () => { isMounted = false; };
  }, [id]);

  // Handle session timers
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Simulate initial tab connection handshake
  useEffect(() => {
    tabs.forEach((tab) => {
      if (tab.connectionStatus === 'connecting' && tab.outputs.length === 0) {
        // Trigger simulated connection banner
        setTimeout(() => {
          updateTab(tab.id, (t) => {
            const isPS = t.type === 'powershell';
            const welcomeBanner: CommandOutput[] = [
              {
                id: Math.random().toString(),
                timestamp: new Date().toLocaleTimeString(),
                type: 'system',
                content: isPS
                  ? `Windows PowerShell\nCopyright (C) Microsoft Corporation. All rights reserved.\n\nLoading Personal and System Profiles took 342ms.\nConnected to agent on ${device?.hostname || 'ENDPOINT-01'} [WinRM / SSH Encrypted Tunnel]\nType 'help' for available commands.`
                  : `Microsoft Windows [Version 10.0.19045.3803]\n(c) Microsoft Corporation. All rights reserved.\n\nConnected to agent on ${device?.hostname || 'ENDPOINT-01'} [CMD Session]\nType 'help' for available commands.`,
              },
            ];
            return {
              ...t,
              connectionStatus: 'connected',
              outputs: welcomeBanner,
            };
          });
        }, 800);
      }
    });
  }, [device?.hostname]);

  // Auto-scroll output on updates
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab?.outputs]);

  // Helper to mutate specific tab
  const updateTab = (tabId: string, updater: (t: TerminalTab) => TerminalTab) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? updater(t) : t)));
  };

  // Switch tab focus
  const handleSelectTab = (tabId: string) => {
    setActiveTabId(tabId);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Add new tab
  const handleAddTab = () => {
    const nextNum = tabs.length + 1;
    const newId = `tab-${Date.now()}`;
    const newTab: TerminalTab = {
      id: newId,
      title: `${newTabType === 'powershell' ? 'PowerShell' : 'CMD'} ${nextNum}`,
      type: newTabType,
      workingDir: 'C:\\Users\\admin',
      outputs: [
        {
          id: Math.random().toString(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          content: newTabType === 'powershell'
            ? `Windows PowerShell\nConnected to ${device?.hostname || 'ENDPOINT-01'} [Session #${nextNum}]`
            : `Microsoft Windows CMD [Connected to ${device?.hostname || 'ENDPOINT-01'}]`,
        },
      ],
      commandHistory: [],
      historyIndex: -1,
      connectionStatus: 'connected',
      exitCode: 0,
      isExecuting: false,
    };
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newId);
    setShowNewTabModal(false);
    toast(`Created new ${newTabType === 'powershell' ? 'PowerShell' : 'CMD'} terminal tab`, 'info');
  };

  // Close tab
  const handleCloseTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tabs.length === 1) {
      toast('Cannot close the last terminal session', 'warning');
      return;
    }
    const remaining = tabs.filter((t) => t.id !== tabId);
    setTabs(remaining);
    if (activeTabId === tabId) {
      setActiveTabId(remaining[remaining.length - 1].id);
    }
  };

  // Reconnect tab
  const handleReconnect = () => {
    if (!activeTab) return;
    updateTab(activeTab.id, (t) => ({
      ...t,
      connectionStatus: 'reconnecting',
      isExecuting: true,
    }));

    setTimeout(() => {
      updateTab(activeTab.id, (t) => ({
        ...t,
        connectionStatus: 'connected',
        isExecuting: false,
        outputs: [
          ...t.outputs,
          {
            id: Math.random().toString(),
            timestamp: new Date().toLocaleTimeString(),
            type: 'system',
            content: `[SYSTEM] Reconnected to remote agent (${device?.hostname || 'ENDPOINT'}). WinRM session re-established.`,
          },
        ],
      }));
      toast('Terminal session reconnected successfully', 'success');
    }, 1200);
  };

  // Command execution logic
  const executeCommand = (cmdStr: string) => {
    const trimmed = cmdStr.trim();
    if (!trimmed || !activeTab) return;

    const currentPrompt = activeTab.type === 'powershell'
      ? `PS ${activeTab.workingDir}> `
      : `${activeTab.workingDir}> `;

    // Push prompt line
    const promptEntry: CommandOutput = {
      id: Math.random().toString(),
      timestamp: new Date().toLocaleTimeString(),
      type: 'prompt',
      content: trimmed,
      promptText: currentPrompt,
    };

    // Update history
    const updatedHistory = [...activeTab.commandHistory, trimmed];

    updateTab(activeTab.id, (t) => ({
      ...t,
      commandHistory: updatedHistory,
      historyIndex: -1,
      isExecuting: true,
      outputs: [...t.outputs, promptEntry],
    }));

    setInputVal('');

    const lower = trimmed.toLowerCase();
    if (lower === 'cls' || lower === 'clear') {
      updateTab(activeTab.id, (t) => ({
        ...t,
        isExecuting: false,
        exitCode: 0,
        outputs: [],
      }));
      return;
    }

    void (async () => {
      try {
        const api = mockApi as any;
        if (typeof api.runDeviceCommand !== 'function' || !device?.id) {
          throw new Error('Remote command API is not available');
        }
        const queued = await api.runDeviceCommand(device.id, {
          shell: activeTab.type,
          script: trimmed,
        });
        const commandId = queued?.commandId || queued?.command_id;
        if (!commandId) {
          throw new Error('Agent did not accept the command');
        }

        let output = '';
        let isError = false;
        let exitCode = 0;
        while (true) {
          await new Promise((r) => setTimeout(r, 1000));
          const status = await api.getDeviceCommand(device.id, commandId);
          if (status?.status === 'acknowledged') {
            output = status.output || status.error || '(no output)';
            isError = Number(status.exitCode) !== 0;
            exitCode = Number(status.exitCode ?? 0);
            break;
          }
        }

        updateTab(activeTab.id, (t) => ({
          ...t,
          isExecuting: false,
          exitCode,
          outputs: [
            ...t.outputs,
            {
              id: Math.random().toString(),
              timestamp: new Date().toLocaleTimeString(),
              type: (isError ? 'error' : 'output') as CommandOutput['type'],
              content: output,
            },
          ],
        }));
      } catch (err: any) {
        updateTab(activeTab.id, (t) => ({
          ...t,
          isExecuting: false,
          exitCode: 1,
          outputs: [
            ...t.outputs,
            {
              id: Math.random().toString(),
              timestamp: new Date().toLocaleTimeString(),
              type: 'error' as CommandOutput['type'],
              content: err?.message || 'Failed to run remote command',
            },
          ],
        }));
      }
    })();
  };

  // Key navigation for command history & enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!activeTab) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      executeCommand(inputVal);
    } else if (e.key === 'ArrowUp' && !inputVal.includes('\n')) {
      e.preventDefault();
      if (activeTab.commandHistory.length === 0) return;
      const nextIdx = activeTab.historyIndex === -1
        ? activeTab.commandHistory.length - 1
        : Math.max(0, activeTab.historyIndex - 1);

      updateTab(activeTab.id, (t) => ({ ...t, historyIndex: nextIdx }));
      setInputVal(activeTab.commandHistory[nextIdx] || '');
    } else if (e.key === 'ArrowDown' && !inputVal.includes('\n')) {
      e.preventDefault();
      if (activeTab.historyIndex === -1) return;
      const nextIdx = activeTab.historyIndex + 1;
      if (nextIdx >= activeTab.commandHistory.length) {
        updateTab(activeTab.id, (t) => ({ ...t, historyIndex: -1 }));
        setInputVal('');
      } else {
        updateTab(activeTab.id, (t) => ({ ...t, historyIndex: nextIdx }));
        setInputVal(activeTab.commandHistory[nextIdx] || '');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Simple tab completion for common commands
      const commonCmds = ['Get-Process', 'Get-Service', 'Get-ChildItem', 'ipconfig', 'systeminfo', 'whoami', 'hostname', 'clear', 'cls'];
      const match = commonCmds.find((c) => c.toLowerCase().startsWith(inputVal.toLowerCase()));
      if (match) setInputVal(match);
    }
  };

  // Actions
  const handleClearTerminal = () => {
    if (!activeTab) return;
    updateTab(activeTab.id, (t) => ({ ...t, outputs: [] }));
    toast('Terminal buffer cleared', 'info');
  };

  const handleCopyOutput = () => {
    if (!activeTab) return;
    const text = activeTab.outputs.map((o) => `${o.promptText || ''}${o.content}`).join('\n');
    navigator.clipboard.writeText(text);
    toast('Terminal output copied to clipboard', 'success');
  };

  const handleDownloadTranscript = () => {
    if (!activeTab) return;
    const text = activeTab.outputs.map((o) => `[${o.timestamp}] ${o.promptText || ''}${o.content}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `terminal_${device?.hostname || 'device'}_${activeTab.type}_${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast('Transcript downloaded successfully', 'success');
  };

  // Render helpers
  const promptString = activeTab?.type === 'powershell'
    ? `PS ${activeTab?.workingDir}> `
    : `${activeTab?.workingDir}> `;

  const isPowershell = activeTab?.type === 'powershell';

  return (
    <div className={cn('flex flex-col h-[calc(100vh-3.5rem)] bg-bg text-text-primary overflow-hidden', isFullscreen && 'fixed inset-0 z-50 h-screen w-screen')}>
      {/* Top Device & Session Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-panel border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-text-muted hover:text-text-primary">
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2">
            <TerminalIcon className={cn('h-4 w-4', isPowershell ? 'text-emerald-400' : 'text-blue-400')} />
            <span className="font-semibold text-sm">{device?.hostname || 'Loading device...'}</span>
            <Badge variant="default" className="font-mono text-xs">
              {device?.os || 'Windows 11 Enterprise'}
            </Badge>
          </div>

          <div className="h-4 w-px bg-border my-auto mx-1" />

          {/* Status badge */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full opacity-75', activeTab?.connectionStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400')} />
              <span className={cn('relative inline-flex rounded-full h-2 w-2', activeTab?.connectionStatus === 'connected' ? 'bg-emerald-500' : 'bg-amber-500')} />
            </span>
            <span className="text-xs text-text-secondary capitalize font-mono">
              {activeTab?.connectionStatus}
            </span>
          </div>

          <div className="h-4 w-px bg-border my-auto mx-1" />

          {/* Working dir */}
          <span className="text-xs font-mono text-text-muted truncate max-w-[200px]" title={activeTab?.workingDir}>
            {activeTab?.workingDir}
          </span>
        </div>

        {/* Right Header info & actions */}
        <div className="flex items-center gap-3">
          {/* Execution status indicator */}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-elevated border border-border text-xs font-mono">
            {activeTab?.isExecuting ? (
              <>
                <RotateCw className="h-3 w-3 animate-spin text-amber-400" />
                <span className="text-amber-400">Executing...</span>
              </>
            ) : (
              <>
                <Check className="h-3 w-3 text-emerald-400" />
                <span className="text-text-muted">Ready</span>
              </>
            )}
            <span className="text-text-disabled mx-1">|</span>
            <span className="text-text-muted">Exit: {activeTab?.exitCode ?? 0}</span>
          </div>

          {/* Session timer */}
          <div className="flex items-center gap-1.5 text-xs font-mono text-text-secondary">
            <Clock className="h-3.5 w-3.5 text-text-muted" />
            <span>{formatDuration(sessionSeconds)}</span>
          </div>

          {/* Search bar toggle button */}
          <Button
            variant={showSearch ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowSearch(!showSearch)}
            title="Search Terminal Output"
          >
            <Search className="h-3.5 w-3.5" />
          </Button>

          {/* History drawer toggle */}
          <Button
            variant={showHistoryPanel ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowHistoryPanel(!showHistoryPanel)}
            title="Command History"
          >
            <History className="h-3.5 w-3.5" />
          </Button>

          {/* Fullscreen toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex items-center justify-between px-2 pt-2 bg-panel border-b border-border shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            const isPS = tab.type === 'powershell';
            return (
              <div
                key={tab.id}
                onClick={() => handleSelectTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-t border-t border-x text-xs font-mono transition-colors cursor-pointer select-none group',
                  isActive
                    ? 'bg-bg border-border text-text-primary font-medium'
                    : 'bg-elevated/50 border-transparent text-text-muted hover:text-text-secondary hover:bg-elevated'
                )}
              >
                <span className={cn('h-2 w-2 rounded-full', isPS ? 'bg-emerald-500' : 'bg-blue-500')} />
                <span>{tab.title}</span>
                {tab.isExecuting && <RotateCw className="h-3 w-3 animate-spin text-amber-400" />}
                <button
                  onClick={(e) => handleCloseTab(tab.id, e)}
                  className="p-0.5 rounded hover:bg-hover hover:text-status-critical opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}

          <button
            onClick={() => setShowNewTabModal(true)}
            className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-hover transition-colors ml-1"
            title="New Terminal Tab"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* Action Toolbar */}
        <div className="flex items-center gap-1 pb-1">
          <Button variant="ghost" size="sm" onClick={handleCopyOutput} title="Copy Output">
            <Copy className="h-3.5 w-3.5 text-text-muted" />
            <span className="hidden sm:inline text-xs">Copy</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDownloadTranscript} title="Download Transcript">
            <Download className="h-3.5 w-3.5 text-text-muted" />
            <span className="hidden sm:inline text-xs">Transcript</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleClearTerminal} title="Clear Terminal">
            <Trash2 className="h-3.5 w-3.5 text-text-muted" />
            <span className="hidden sm:inline text-xs">Clear</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReconnect} title="Reconnect Session">
            <RotateCw className="h-3.5 w-3.5 text-text-muted" />
            <span className="hidden sm:inline text-xs">Reconnect</span>
          </Button>
        </div>
      </div>

      {/* Session status */}
      <div className="bg-elevated px-4 py-1 border-b border-border flex items-center justify-between text-2xs text-text-muted font-mono shrink-0">
        <span>PowerShell and CMD run with no timeout or command filter</span>
        <div className="flex items-center gap-2">
          <Shield className="h-3 w-3 text-emerald-400" />
          <span>Audit Logging Active</span>
        </div>
      </div>

      {/* Optional Search Overlay Bar */}
      {showSearch && (
        <div className="bg-elevated px-4 py-2 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Search className="h-4 w-4 text-text-muted shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search terminal output text..."
              className="bg-bg border border-border rounded px-2.5 py-1 text-xs text-text-primary focus:outline-none focus:border-accent w-full font-mono"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-text-muted font-mono">
            {searchQuery && (
              <span>
                Matches found: {activeTab?.outputs.filter((o) => o.content.toLowerCase().includes(searchQuery.toLowerCase())).length}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setShowSearch(false); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Terminal Body Area (Split with history drawer if open) */}
      <div className="flex-1 flex overflow-hidden relative">
        <div
          className={cn(
            'flex-1 flex flex-col bg-[#0a0a0b] p-4 font-mono text-xs overflow-y-auto selection:bg-accent/30',
            isPowershell ? 'text-emerald-400' : 'text-zinc-300'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {loadingDevice ? (
            <div className="flex items-center gap-2 text-text-muted py-4">
              <RotateCw className="h-4 w-4 animate-spin text-accent" />
              <span>Connecting terminal tunnel to endpoint...</span>
            </div>
          ) : (
            <>
              {/* Render Output Lines */}
              {activeTab?.outputs.map((out) => {
                const matchesSearch = searchQuery && out.content.toLowerCase().includes(searchQuery.toLowerCase());
                return (
                  <div
                    key={out.id}
                    className={cn(
                      'mb-1.5 whitespace-pre-wrap leading-relaxed break-all',
                      matchesSearch && 'bg-amber-500/20 p-1 rounded border border-amber-500/40 text-amber-200'
                    )}
                  >
                    {out.type === 'prompt' && (
                      <div className="flex items-start">
                        <span className="text-zinc-500 select-none mr-2 font-semibold">
                          {out.promptText}
                        </span>
                        <span className="text-text-primary font-medium">{out.content}</span>
                      </div>
                    )}
                    {out.type === 'system' && (
                      <div className="text-zinc-500 italic border-l-2 border-zinc-700 pl-2 my-1">
                        {out.content}
                      </div>
                    )}
                    {out.type === 'error' && (
                      <div className="text-red-400 bg-red-950/20 p-2 rounded border border-red-900/30 my-1 font-mono">
                        {out.content}
                      </div>
                    )}
                    {out.type === 'output' && (
                      <div className={cn(isPowershell ? 'text-emerald-400' : 'text-zinc-300')}>
                        {out.content}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Active Command Prompt Line */}
              <div className="flex items-start mt-1 pt-1 border-t border-zinc-900/80">
                <span className="text-zinc-400 select-none mr-1.5 font-semibold shrink-0 pt-0.5">
                  {promptString}
                </span>
                <textarea
                  ref={inputRef}
                  rows={Math.min(12, Math.max(1, inputVal.split('\n').length))}
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={activeTab?.isExecuting || activeTab?.connectionStatus !== 'connected'}
                  placeholder={activeTab?.isExecuting ? 'Command running...' : 'Paste a script. Enter to run, Shift+Enter for a new line.'}
                  className={cn(
                    'flex-1 bg-transparent border-none outline-none font-mono text-xs focus:ring-0 p-0 resize-none leading-5',
                    isPowershell ? 'text-emerald-300 placeholder-emerald-800' : 'text-zinc-100 placeholder-zinc-700'
                  )}
                  autoFocus
                />
              </div>

              <div ref={outputEndRef} />
            </>
          )}
        </div>

        {/* Collapsible History Drawer */}
        {showHistoryPanel && (
          <div className="w-72 bg-panel border-l border-border flex flex-col h-full shrink-0 animate-slide-up">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold">Command History</span>
              </div>
              <button onClick={() => setShowHistoryPanel(false)} className="text-text-muted hover:text-text-primary">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="p-2 flex-1 overflow-y-auto space-y-1">
              {activeTab?.commandHistory.length === 0 ? (
                <div className="text-xs text-text-muted p-4 text-center">
                  No commands executed in this session yet.
                </div>
              ) : (
                activeTab?.commandHistory.map((cmd, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setInputVal(cmd);
                      inputRef.current?.focus();
                    }}
                    className="p-2 rounded bg-elevated/60 hover:bg-hover cursor-pointer border border-border/50 text-xs font-mono text-text-secondary hover:text-text-primary transition-colors flex items-center justify-between group"
                  >
                    <span className="truncate flex-1">{cmd}</span>
                    <CornerDownLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 text-accent ml-2 shrink-0" />
                  </div>
                ))
              )}
            </div>

            <div className="p-3 border-t border-border flex items-center justify-between">
              <span className="text-2xs text-text-muted">
                {activeTab?.commandHistory.length || 0} items
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateTab(activeTab.id, (t) => ({ ...t, commandHistory: [] }))}
                className="text-2xs text-status-critical"
              >
                Clear History
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer Session Audit Bar */}
      <div className="px-4 py-2 bg-panel border-t border-border flex flex-wrap items-center justify-between text-2xs text-text-muted font-mono shrink-0 gap-2">
        <div className="flex items-center gap-4">
          <span>Session ID: <strong className="text-text-secondary">TRM-8891-A2</strong></span>
          <span>Technician: <strong className="text-text-secondary">admin (System Admin)</strong></span>
          <span>IP: <strong className="text-text-secondary">{device?.publicIp || '192.168.1.100'}</strong></span>
        </div>
        <div className="flex items-center gap-3">
          <span>Protocol: <strong className="text-text-secondary">WinRM / SSH Encrypted</strong></span>
          <span className="text-emerald-400 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Audit Logged
          </span>
        </div>
      </div>

      {/* New Tab Creation Modal */}
      <Modal
        open={showNewTabModal}
        onClose={() => setShowNewTabModal(false)}
        title="Open New Terminal Tab"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowNewTabModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleAddTab}>
              Create Tab
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-xs">
          <p className="text-text-secondary">
            Select shell environment type for the new terminal session on <strong>{device?.hostname || 'Device'}</strong>:
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => setNewTabType('powershell')}
              className={cn(
                'p-3 rounded-lg border cursor-pointer flex flex-col gap-2 transition-all',
                newTabType === 'powershell'
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-elevated hover:bg-hover'
              )}
            >
              <div className="flex items-center gap-2">
                <TerminalIcon className="h-4 w-4 text-emerald-400" />
                <span className="font-semibold text-text-primary">PowerShell</span>
              </div>
              <span className="text-2xs text-text-muted">Object-oriented shell & scripting language</span>
            </div>

            <div
              onClick={() => setNewTabType('cmd')}
              className={cn(
                'p-3 rounded-lg border cursor-pointer flex flex-col gap-2 transition-all',
                newTabType === 'cmd'
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-elevated hover:bg-hover'
              )}
            >
              <div className="flex items-center gap-2">
                <TerminalIcon className="h-4 w-4 text-blue-400" />
                <span className="font-semibold text-text-primary">CMD</span>
              </div>
              <span className="text-2xs text-text-muted">Windows Command Prompt classic environment</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Mock Command Response Generator ──
interface MockCmdResult {
  text: string;
  exitCode: number;
  isError?: boolean;
  workingDirChange?: string;
  clearScreen?: boolean;
}

function responseDelay(cmd: string): number {
  const c = cmd.trim().toLowerCase();
  if (c.startsWith('ping') || c.startsWith('systeminfo')) return 600;
  return 200;
}

function generateMockResponse(cmdStr: string, tab: TerminalTab, device: Device | null): MockCmdResult {
  const parts = cmdStr.trim().split(/\s+/);
  const mainCmd = parts[0].toLowerCase();
  const arg1 = parts[1];

  switch (mainCmd) {
    case 'help':
      return {
        exitCode: 0,
        text: `Available Commands in InsightNode Remote Terminal:
  help                     - Display this help summary
  dir / ls / Get-ChildItem - List directory contents
  cd <path>                - Change current working directory
  ipconfig / Get-NetIP     - Show IP network configuration
  systeminfo               - Display detailed device & OS properties
  whoami                   - Print current logged-in user context
  hostname                 - Display endpoint device hostname
  Get-Process / tasklist   - List active running processes
  Get-Service / net start  - List Windows system services status
  ping <host>              - Test network connectivity
  echo <text>              - Echo text input
  clear / cls              - Clear terminal buffer
  exit                     - End session`,
      };

    case 'clear':
    case 'cls':
      return { text: '', exitCode: 0, clearScreen: true };

    case 'hostname':
      return { text: device?.hostname || 'INSIGHTNODE-SRV01', exitCode: 0 };

    case 'whoami':
      return { text: `${device?.client.toUpperCase() || 'INSIGHTNODE'}\\admin_tech`, exitCode: 0 };

    case 'echo':
      return { text: parts.slice(1).join(' '), exitCode: 0 };

    case 'dir':
    case 'ls':
    case 'get-childitem':
      return {
        exitCode: 0,
        text: `Directory: ${tab.workingDir}

Mode          LastWriteTime         Length Name
----          -------------         ------ ----
d-----   ${formatDate(new Date(Date.now() - 86400000 * 30).toISOString())}          0 Program Files
d-----   ${formatDate(new Date(Date.now() - 86400000 * 15).toISOString())}          0 Program Files (x86)
d-----   ${formatDate(new Date(Date.now() - 86400000 * 5).toISOString())}          0 Users
d-----   ${formatDate(new Date(Date.now() - 86400000 * 2).toISOString())}          0 Windows
-a----   ${formatDate(new Date(Date.now() - 3600000 * 4).toISOString())}       2048 agent_config.json
-a----   ${formatDate(new Date(Date.now() - 3600000 * 12).toISOString())}     1048576 insightnode_audit.log
-a----   ${formatDate(new Date(Date.now() - 86400000 * 10).toISOString())}     5242880 backup_status.xml`,
      };

    case 'cd':
    case 'set-location':
      if (!arg1 || arg1 === '~') {
        return { text: '', exitCode: 0, workingDirChange: 'C:\\Users\\admin' };
      }
      if (arg1 === '..') {
        const segments = tab.workingDir.split('\\').filter(Boolean);
        if (segments.length > 1) {
          segments.pop();
          return { text: '', exitCode: 0, workingDirChange: segments.join('\\') };
        }
        return { text: '', exitCode: 0, workingDirChange: 'C:' };
      }
      const newPath = arg1.includes(':') ? arg1 : `${tab.workingDir}\\${arg1}`;
      return { text: '', exitCode: 0, workingDirChange: newPath };

    case 'ipconfig':
    case 'ifconfig':
    case 'get-netipaddress':
      return {
        exitCode: 0,
        text: `Windows IP Configuration

Ethernet adapter Ethernet0:
   Connection-specific DNS Suffix  . : localdomain
   IPv4 Address. . . . . . . . . . . : ${device?.localIp || '192.168.1.145'}
   Subnet Mask . . . . . . . . . . . : 255.255.255.0
   Default Gateway . . . . . . . . . : 192.168.1.1
   MAC Address . . . . . . . . . . . : 00-15-5D-01-A4-BC

Public Gateway Address . . . . . . . : ${device?.publicIp || '198.51.100.42'}`,
      };

    case 'systeminfo':
    case 'get-computerinfo':
      return {
        exitCode: 0,
        text: `Host Name:                 ${device?.hostname || 'INSIGHTNODE-SRV01'}
OS Name:                   ${device?.os || 'Microsoft Windows 11 Enterprise'}
OS Version:                ${device?.osVersion || '10.0.19045 N/A Build 19045'}
System Manufacturer:       ${device?.manufacturer || 'Dell Inc.'}
System Model:              ${device?.model || 'OptiPlex 7090'}
System Type:               x64-based PC
Processor(s):              ${device?.cpuModel || 'Intel Core i7-11700 @ 2.50GHz'} (${device?.cpuCores || 8} Cores)
Total Physical Memory:     ${device?.memoryTotal ? `${Math.round(device.memoryTotal / 1024)} GB` : '16 GB'}
Available Physical Memory: ${device?.memoryUsed ? `${Math.round((device.memoryTotal - device.memoryUsed) / 1024)} GB` : '8 GB'}
Network Card(s):           1 NIC(s) Installed. [01]: ${device?.localIp || '192.168.1.145'}
Agent Version:             ${device?.agentVersion || 'v2.4.12'}
Uptime:                    ${device?.uptime || '14 days 6 hours'}`,
      };

    case 'get-process':
    case 'tasklist':
      return {
        exitCode: 0,
        text: `Image Name                     PID Session Name        Session#    Mem Usage
========================= ======== ================ =========== ============
System Idle Process              0 Services                   0          8 K
System                           4 Services                   0        156 K
smss.exe                       412 Services                   0      1,024 K
csrss.exe                      580 Services                   0      4,210 K
services.exe                   744 Services                   0      9,840 K
lsass.exe                      760 Services                   0     18,450 K
insightnode-agent.exe         1420 Services                   0     42,180 K
svchost.exe                   2104 Services                   0     24,510 K
explorer.exe                  3890 Console                    1    112,040 K`,
      };

    case 'get-service':
    case 'net':
      return {
        exitCode: 0,
        text: `Status   Name               DisplayName
------   ----               -----------
Running  InsightNodeAgent   InsightNode RMM Management Agent
Running  WinDefend          Microsoft Defender Antivirus Service
Running  wuauserv           Windows Update
Stopped  Spooler            Print Spooler
Running  Dhcp               DHCP Client`,
      };

    case 'ping':
      const target = arg1 || '8.8.8.8';
      return {
        exitCode: 0,
        text: `Pinging ${target} with 32 bytes of data:
Reply from ${target}: bytes=32 time=14ms TTL=118
Reply from ${target}: bytes=32 time=12ms TTL=118
Reply from ${target}: bytes=32 time=16ms TTL=118
Reply from ${target}: bytes=32 time=13ms TTL=118

Ping statistics for ${target}:
    Packets: Sent = 4, Received = 4, Lost = 0 (0% loss),
Approximate round trip times in milli-seconds:
    Minimum = 12ms, Maximum = 16ms, Average = 13ms`,
      };

    case 'exit':
      return {
        exitCode: 0,
        text: `[SYSTEM] Terminal session closed by user. Connection terminated.`,
      };

    default:
      return {
        exitCode: 1,
        isError: true,
        text: tab.type === 'powershell'
          ? `'${cmdStr}' is not recognized as the name of a cmdlet, function, script file, or operable program. Check the spelling of the name, or if a path was included, verify that the path is correct and try again.`
          : `'${cmdStr}' is not recognized as an internal or external command, operable program or batch file.`,
      };
  }
}
